import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CheckboxData, Checkboxes } from "mui-rff";
import { useMemo } from "react";
import { useField } from "react-final-form";
import { FieldState } from "final-form";

import i18n, { useTranslation, useLocale } from "@/lib/i18n";
import { BasicWidgetFormProps } from "@/features/widget-forms/dialogs/widget-dialog-types";
import {
  isCoastalMaritimeArea,
  useMaritimeAreasQuery,
} from "@/features/dashboard/components/warnings/maritime-areas.queries";
import { getAreaName } from "@/features/dashboard/components/warnings/maritime-warnings.utils";
import { normalizeMaritimeForecastConfiguration } from "@/lib/api";

export function MaritimeForecastWidgetForm({ values }: BasicWidgetFormProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { data: areas = [], isLoading, isError } = useMaritimeAreasQuery();

  const initialConfiguration = useMemo(
    () => normalizeMaritimeForecastConfiguration(values?.configuration),
    [values?.configuration],
  );

  useField("configuration.area_ids", {
    initialValue: initialConfiguration.area_ids,
  });

  const areaCheckboxData = useMemo((): CheckboxData[] => {
    return areas.filter(isCoastalMaritimeArea).map((area) => ({
      label: getAreaName(area, locale),
      value: area.id,
    }));
  }, [areas, locale]);

  if (isLoading) {
    return (
      <Stack mt={2}>
        <Typography variant="body2">
          {t("admin.widgets.fields.maritime_forecast.area_ids.loading", {
            defaultValue: "Loading areas…",
          })}
        </Typography>
      </Stack>
    );
  }

  if (isError || !areaCheckboxData.length) {
    return (
      <Stack mt={2}>
        <Typography variant="body2" color="error">
          {t("admin.widgets.fields.maritime_forecast.area_ids.load_error", {
            defaultValue: "Could not load maritime areas.",
          })}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack mt={2}>
      <Checkboxes
        name="configuration.area_ids"
        label={t("admin.widgets.fields.maritime_forecast.area_ids.label")}
        data={areaCheckboxData}
        fieldProps={{ validate: maritimeForecastAreaIdsRequired }}
      />
    </Stack>
  );
}

function maritimeForecastAreaIdsRequired(
  value: unknown,
  _allValues: object,
  meta?: FieldState<unknown>,
) {
  if (meta && meta.pristine) return;
  if (!Array.isArray(value) || value.length === 0) {
    return i18n.t("admin.widgets.fields.maritime_forecast.area_ids.validation_required");
  }
}
